import type { CardColorType } from "@shared/models/card-stack"
import type { CardId } from "@shared/unified-tiptap/analytics/types"
import type { ExtensionCardGoogleClassroomAttrs } from "@shared/unified-tiptap/extensions/card-google-classroom/card-google-classroom-extension"
import type { ExtensionCardTitleAttrs } from "@shared/unified-tiptap/extensions/card-title/card-title-extension"
import { extractLessonStandards } from "@shared/unified-tiptap/extensions/standard/standard-extension"
import { getCardId, type CardAttrsBase } from "@shared/unified-tiptap/util/card"
import type { Node as PMNode } from "@tiptap/pm/model"

export interface CardChange {
  readonly cardId: CardId
  readonly kind: "added" | "removed" | "moved" | "edited"
  readonly titleWordsAdded: number
  readonly titleWordsRemoved: number
  readonly bodyWordsAdded: number
  readonly bodyWordsRemoved: number
  /**
   * Of `bodyWordsAdded`, the words a card removed in the same window also had — a merge
   * survivor's absorbed body. What is left over is what someone typed.
   */
  readonly bodyWordsAbsorbed: number
  readonly attachmentsAdded: number
  readonly attachmentsRemoved: number
  readonly standardsAdded: number
  readonly standardsRemoved: number
  readonly linksAdded: number
  readonly linksRemoved: number
  readonly colorChanged: boolean
  readonly isPublicChanged: boolean
}

export interface CardStackChangeSummary {
  readonly cards: readonly CardChange[]
}

/**
 * Diffs two CardStack ProseMirror docs into the per-card deltas the editor's version
 * narrative reports. A leading `lessonTitle` is skipped, so the editor's own doc and
 * `CardStack.proseMirror` (cards only) are both accepted; the lesson title is its own
 * action's narrative (LESSON_EDIT_TITLE).
 *
 * Unchanged cards are omitted. A card that both moved and changed reports `"moved"`; its
 * deltas are still filled in.
 */
export function summarizeCardStackChange(before: PMNode, after: PMNode): CardStackChangeSummary {
  const beforeCards = snapshotCards(before)
  const afterCards = snapshotCards(after)
  const stayedInOrder = cardsThatStayedInOrder(beforeCards, afterCards)
  const removedWords = wordCounts(
    [...beforeCards]
      .filter(([cardId]) => !afterCards.has(cardId))
      .map(([, card]) => card.body)
      .join(" "),
  )

  const cards: CardChange[] = []
  for (const [cardId, afterCard] of afterCards) {
    const beforeCard = beforeCards.get(cardId)
    if (beforeCard === undefined) {
      cards.push(buildChange(cardId, "added", null, afterCard, removedWords))
      continue
    }
    const change = buildChange(cardId, "edited", beforeCard, afterCard, removedWords)
    if (!stayedInOrder.has(cardId)) cards.push({ ...change, kind: "moved" })
    else if (hasChange(change)) cards.push(change)
  }
  for (const [cardId, beforeCard] of beforeCards) {
    if (!afterCards.has(cardId)) {
      cards.push(buildChange(cardId, "removed", beforeCard, null, removedWords))
    }
  }

  return { cards }
}

export function isEmptySummary(s: CardStackChangeSummary): boolean {
  return s.cards.length === 0
}

/** "Edited 3 cards, adding 7 words and removing 8 words." A clause for zero words is left out. */
export function describeChange(summary: CardStackChangeSummary): string {
  const cards = summary.cards
  const added = sumBy(cards, (c) => c.titleWordsAdded + c.bodyWordsAdded)
  const removed = sumBy(cards, (c) => c.titleWordsRemoved + c.bodyWordsRemoved)

  const clauses: string[] = []
  if (added > 0) clauses.push(`adding ${pluralize(added, "word")}`)
  if (removed > 0) clauses.push(`removing ${pluralize(removed, "word")}`)

  const edited = `Edited ${pluralize(cards.length, "card")}`
  return clauses.length === 0 ? `${edited}.` : `${edited}, ${clauses.join(" and ")}.`
}

const ATTACHMENT_NODE_NAMES = new Set(["image", "linkBlock"])

interface CardSnapshot {
  readonly index: number
  readonly title: string
  readonly body: string
  readonly attachments: number
  readonly links: number
  readonly standardIds: readonly string[]
  readonly color: CardColorType
  readonly isPublic: boolean
}

function snapshotCards(doc: PMNode): Map<CardId, CardSnapshot> {
  const cards = new Map<CardId, CardSnapshot>()
  let index = 0
  doc.forEach((node) => {
    const cardId = getCardId(node)
    if (cardId === null) return
    cards.set(cardId, snapshotCard(node, index++))
  })
  return cards
}

function snapshotCard(card: PMNode, index: number): CardSnapshot {
  if (card.type.name === "cardGoogleClassroom") {
    const attrs = card.attrs as ExtensionCardGoogleClassroomAttrs
    return {
      index,
      title: attrs.title,
      body: attrs.value,
      attachments: attrs.attachments.length,
      links: 0,
      standardIds: [],
      color: attrs.color,
      isPublic: attrs.isPublic,
    }
  }

  // titledCard or untitledCard case

  const titleNode = card.firstChild?.type.name === "cardTitle" ? card.firstChild : null
  const titleAttrs = titleNode?.attrs as ExtensionCardTitleAttrs | undefined

  const bodyParts: string[] = []
  card.forEach((child) => {
    if (child !== titleNode) bodyParts.push(nodeText(child))
  })

  let attachments = 0
  let links = 0
  card.descendants((node) => {
    if (ATTACHMENT_NODE_NAMES.has(node.type.name)) attachments++
    else if (node.type.name === "linkInline") links++
  })

  return {
    index,
    title: titleNode === null ? "" : nodeText(titleNode),
    body: bodyParts.join(" "),
    attachments,
    links,
    standardIds: extractLessonStandards(card).map((standard) => standard.id),
    color: titleAttrs?.color ?? "yellow",
    isPublic: (card.attrs as CardAttrsBase).isPublic,
  }
}

/** Block-separated, so adjacent paragraphs don't fuse their end and start words. */
function nodeText(node: PMNode): string {
  return node.textBetween(0, node.content.size, " ")
}

function buildChange(
  cardId: CardId,
  kind: CardChange["kind"],
  before: CardSnapshot | null,
  after: CardSnapshot | null,
  removedWords: ReadonlyMap<string, number>,
): CardChange {
  const title = wordDelta(before?.title ?? "", after?.title ?? "")
  const body = wordDelta(before?.body ?? "", after?.body ?? "")
  const standards = idDelta(before?.standardIds ?? [], after?.standardIds ?? [])
  const bothPresent = before !== null && after !== null

  return {
    cardId,
    kind,
    titleWordsAdded: title.added,
    titleWordsRemoved: title.removed,
    bodyWordsAdded: body.added,
    bodyWordsRemoved: body.removed,
    bodyWordsAbsorbed: countMatching(body.addedWords, removedWords),
    attachmentsAdded: Math.max(0, (after?.attachments ?? 0) - (before?.attachments ?? 0)),
    attachmentsRemoved: Math.max(0, (before?.attachments ?? 0) - (after?.attachments ?? 0)),
    standardsAdded: standards.added,
    standardsRemoved: standards.removed,
    linksAdded: Math.max(0, (after?.links ?? 0) - (before?.links ?? 0)),
    linksRemoved: Math.max(0, (before?.links ?? 0) - (after?.links ?? 0)),
    colorChanged: bothPresent && before.color !== after.color,
    isPublicChanged: bothPresent && before.isPublic !== after.isPublic,
  }
}

function hasChange(change: CardChange): boolean {
  return (
    change.titleWordsAdded > 0 ||
    change.titleWordsRemoved > 0 ||
    change.bodyWordsAdded > 0 ||
    change.bodyWordsRemoved > 0 ||
    change.attachmentsAdded > 0 ||
    change.attachmentsRemoved > 0 ||
    change.standardsAdded > 0 ||
    change.standardsRemoved > 0 ||
    change.linksAdded > 0 ||
    change.linksRemoved > 0 ||
    change.colorChanged ||
    change.isPublicChanged
  )
}

interface Delta {
  readonly added: number
  readonly removed: number
}

interface WordDelta extends Delta {
  /** The `after` words left unmatched by `before` — the ones `added` counts. */
  readonly addedWords: readonly string[]
}

/**
 * Word counts are multiset deltas, not a positional diff: retyping a word elsewhere in
 * the same card nets to zero, which is what "words added" should mean for a metric.
 */
function wordDelta(before: string, after: string): WordDelta {
  const unmatched = wordCounts(before)

  const addedWords: string[] = []
  for (const word of words(after)) {
    const remaining = unmatched.get(word) ?? 0
    if (remaining > 0) unmatched.set(word, remaining - 1)
    else addedWords.push(word)
  }

  let removed = 0
  for (const remaining of unmatched.values()) removed += remaining
  return { added: addedWords.length, removed, addedWords }
}

function wordCounts(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  for (const word of words(text)) counts.set(word, (counts.get(word) ?? 0) + 1)
  return counts
}

/** How many of `sought` the pool holds, each pool entry answering for at most one word. */
function countMatching(sought: readonly string[], pool: ReadonlyMap<string, number>): number {
  const remaining = new Map(pool)
  let matched = 0
  for (const word of sought) {
    const left = remaining.get(word) ?? 0
    if (left === 0) continue
    remaining.set(word, left - 1)
    matched++
  }
  return matched
}

function words(text: string): string[] {
  return text.split(/\s+/).filter((word) => word.length > 0)
}

function idDelta(before: readonly string[], after: readonly string[]): Delta {
  const beforeIds = new Set(before)
  const afterIds = new Set(after)
  let added = 0
  for (const id of afterIds) if (!beforeIds.has(id)) added++
  let removed = 0
  for (const id of beforeIds) if (!afterIds.has(id)) removed++
  return { added, removed }
}

/**
 * The surviving cards whose relative order is unchanged — the longest common
 * subsequence of the two orderings. Everything else moved. Comparing raw indexes instead
 * would call every card below an insertion "moved".
 */
function cardsThatStayedInOrder(
  before: ReadonlyMap<CardId, CardSnapshot>,
  after: ReadonlyMap<CardId, CardSnapshot>,
): Set<CardId> {
  const beforeOrder = [...before.keys()].filter((id) => after.has(id))
  const afterOrder = [...after.keys()].filter((id) => before.has(id))
  return new Set(longestCommonSubsequence(beforeOrder, afterOrder))
}

function longestCommonSubsequence<T>(a: readonly T[], b: readonly T[]): T[] {
  const lengths: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  )
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lengths[i][j] =
        a[i] === b[j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1])
    }
  }

  const common: T[] = []
  let i = 0
  let j = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      common.push(a[i])
      i++
      j++
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      i++
    } else {
      j++
    }
  }
  return common
}

export function sumBy(cards: readonly CardChange[], of: (card: CardChange) => number): number {
  return cards.reduce((total, card) => total + of(card), 0)
}

export function countBy(
  cards: readonly CardChange[],
  matches: (card: CardChange) => boolean,
): number {
  return cards.reduce((total, card) => total + (matches(card) ? 1 : 0), 0)
}

function pluralize(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`
}
